import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { chatService } from '@services';
import authService from '../services/authServiceFactory';
import config from '../config';
import { ChatRoom, Notification } from '@app-types';

// Types for our real-time features
export interface Message {
  id: number;
  roomId?: number; // Added roomId property for real-time events
  content: string;
  messageType: 'text' | 'system' | 'file' | 'assessment_update';
  metadata?: Record<string, unknown>;
  isEdited: boolean;
  editedAt?: string;
  replyToId?: number;
  createdAt: string;
  isReadByUser: boolean;
  sender: {
    id: number;
    firstName: string;
    lastName: string;
    username: string;
    roles: string[]; // Multi-role support
  };
  replyTo?: {
    content: string;
    senderName: string;
  };
}

export interface UserPresence {
  userId: number;
  status: 'online' | 'away' | 'busy' | 'offline';
  timestamp: string;
}

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;

  // Chat functionality
  chatRooms: ChatRoom[];
  currentRoom: number | null;
  messages: { [roomId: number]: Message[] };
  unreadMessageCounts: { [roomId: number]: number };

  // Notification functionality
  notifications: Notification[];
  unreadNotificationCount: number;

  // Presence functionality
  userPresence: { [userId: number]: UserPresence };

  // Actions
  sendMessage: (roomId: number, content: string, replyToId?: number) => void;
  joinRoom: (roomId: number) => void;
  leaveRoom: (roomId: number) => void;
  markMessagesAsRead: (roomId: number, messageIds: number[]) => void;
  markNotificationAsRead: (notificationId: number) => void;
  updatePresence: (status: 'online' | 'away' | 'busy') => void;
  refreshChatRooms: () => Promise<void>;

  // Loading states
  isLoadingRooms: boolean;
  isLoadingMessages: boolean;
  isLoadingNotifications: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Chat state
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [currentRoom, setCurrentRoom] = useState<number | null>(null);
  const [messages, setMessages] = useState<{ [roomId: number]: Message[] }>({});
  const [unreadMessageCounts, setUnreadMessageCounts] = useState<{ [roomId: number]: number }>({});

  // Notification state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  // Presence state
  const [userPresence, setUserPresence] = useState<{ [userId: number]: UserPresence }>({});

  // Loading states
  const [isLoadingRooms, setIsLoadingRooms] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  // Initialize socket connection
  useEffect(() => {
    if (isAuthenticated && user && !socket) {
      // Use config.apiBaseUrl instead of hardcoded localhost
      const apiUrl = config.apiBaseUrl;

      // Determine Socket.io URL based on environment
      let socketUrl;
      if (process.env.REACT_APP_SOCKET_URL) {
        // Use explicit Socket.io URL if provided
        socketUrl = process.env.REACT_APP_SOCKET_URL;
      } else {
        // Use API base URL without /api suffix
        socketUrl = apiUrl.replace('/api', '');
      }

      const baseUrl = socketUrl;

      const newSocket = io(baseUrl, {
        // Called on every (re)connection attempt so an expired access token
        // is refreshed instead of being reused from the first handshake.
        auth: (cb) => {
          authService
            .then((service) => service.getAccessToken())
            .then((token) => cb({ token }))
            .catch((error) => {
              console.error('Error getting access token for socket connection:', error);
              cb({ token: null });
            });
        },
        autoConnect: true
      });

      newSocket.on('connect', () => {
        console.log('Connected to Socket.io server');
        setIsConnected(true);
        loadInitialData();
      });

      newSocket.on('disconnect', () => {
        console.log('Disconnected from Socket.io server');
        setIsConnected(false);
      });

      // Chat event listeners
      newSocket.on('new_message', handleNewMessage);
      newSocket.on('room_messages', handleRoomMessages);
      newSocket.on('messages_read', handleMessagesRead);
      newSocket.on('unread_message_counts', handleUnreadMessageCounts);

      // Notification event listeners
      newSocket.on('new_notification', handleNewNotification);
      newSocket.on('unread_notifications', handleUnreadNotifications);
      newSocket.on('notification_read', handleNotificationRead);
      newSocket.on('unread_count', handleUnreadCount);

      // Presence event listeners
      newSocket.on('presence_update', handlePresenceUpdate);

      // Error handling
      newSocket.on('error', (error: unknown) => {
        console.error('Socket error:', error);
      });

      setSocket(newSocket);
    }

    return () => {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]); // Only depend on user ID to prevent socket recreation loop

  // Load initial data when connected
  const loadInitialData = async () => {
    try {
      setIsLoadingRooms(true);
      setIsLoadingNotifications(true);

      // Load chat rooms
      const roomsResponse = await chatService.getChatRooms();
      if (roomsResponse.success) {
        setChatRooms(roomsResponse.data);

        // Initialize unread counts
        const counts: { [roomId: number]: number } = {};
        roomsResponse.data.forEach((room: ChatRoom) => {
          counts[room.id] = room.unreadCount;
        });
        setUnreadMessageCounts(counts);
      }

      // Load notifications
      const notificationsResponse = await chatService.getNotifications(20);
      if (notificationsResponse.success) {
        setNotifications(notificationsResponse.data.notifications);
      }

      // Load unread count
      const unreadCountResponse = await chatService.getUnreadNotificationCount();
      if (unreadCountResponse.success) {
        setUnreadNotificationCount(unreadCountResponse.data.count);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setIsLoadingRooms(false);
      setIsLoadingNotifications(false);
    }
  };

  // Public method to refresh chat rooms (can be called when assessment context changes)
  const refreshChatRooms = useCallback(async () => {
    try {
      const roomsResponse = await chatService.getChatRooms();
      if (roomsResponse.success) {
        setChatRooms(roomsResponse.data);

        // Update unread counts
        const counts: { [roomId: number]: number } = {};
        roomsResponse.data.forEach((room: ChatRoom) => {
          counts[room.id] = room.unreadCount;
        });
        setUnreadMessageCounts(counts);
      }
    } catch (error) {
      console.error('Error refreshing chat rooms:', error);
    }
  }, []);

  // Event handlers
  const handleNewMessage = useCallback(
    (message: Message) => {
      setMessages((prev) => ({
        ...prev,
        [message.roomId || 0]: [...(prev[message.roomId || 0] || []), message]
      }));

      // Update unread count if not in current room
      if (currentRoom !== message.roomId && message.sender.id !== user?.id) {
        setUnreadMessageCounts((prev) => ({
          ...prev,
          [message.roomId || 0]: (prev[message.roomId || 0] || 0) + 1
        }));
      }

      // Update room's last message
      setChatRooms((prev) =>
        prev.map((room) =>
          room.id === message.roomId
            ? {
                ...room,
                lastMessage: message.content,
                lastMessageType: message.messageType,
                lastMessageAt: message.createdAt
              }
            : room
        )
      );
    },
    [currentRoom, user?.id]
  );

  const handleRoomMessages = useCallback((data: { roomId: number; messages: Message[] }) => {
    setMessages((prev) => ({
      ...prev,
      [data.roomId]: data.messages
    }));
  }, []);

  const handleMessagesRead = useCallback(
    (data: { userId: number; messageIds: number[]; readAt: string }) => {
      if (data.userId !== user?.id) return;

      // Update local read status
      setMessages((prev) => {
        const updated: { [roomId: number]: Message[] } = {};

        for (const roomId in prev) {
          const parsedRoomId = Number.parseInt(roomId);
          updated[parsedRoomId] = prev[parsedRoomId].map((msg) =>
            data.messageIds.includes(msg.id) ? { ...msg, isReadByUser: true } : msg
          );
        }

        return updated;
      });
    },
    [user?.id]
  );

  const handleUnreadMessageCounts = useCallback((counts: { [roomId: number]: number }) => {
    setUnreadMessageCounts(counts);
  }, []);

  const handleNewNotification = useCallback((notification: Notification) => {
    setNotifications((prev) => [notification, ...prev]);
    setUnreadNotificationCount((prev) => prev + 1);
  }, []);

  const handleUnreadNotifications = useCallback((unreadNotifications: Notification[]) => {
    setNotifications((prev) => [...unreadNotifications, ...prev.filter((n) => n.isRead)]);
  }, []);

  const handleNotificationRead = useCallback((data: { notificationId: number }) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === data.notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
    );
    setUnreadNotificationCount((prev) => Math.max(0, prev - 1));
  }, []);

  const handleUnreadCount = useCallback((data: { count: number }) => {
    setUnreadNotificationCount(data.count);
  }, []);

  const handlePresenceUpdate = useCallback((presence: UserPresence) => {
    setUserPresence((prev) => ({
      ...prev,
      [presence.userId]: presence
    }));
  }, []);

  // Actions
  const sendMessage = useCallback(
    (roomId: number, content: string, replyToId?: number) => {
      if (socket && content.trim()) {
        socket.emit('send_message', {
          roomId,
          content: content.trim(),
          messageType: 'text',
          replyToId
        });
      }
    },
    [socket]
  );

  const joinRoom = useCallback(
    (roomId: number) => {
      if (socket && isConnected) {
        socket.emit('join_room', { roomId });
        setCurrentRoom(roomId);

        // Load messages if not already loaded
        if (!messages[roomId]) {
          setIsLoadingMessages(true);
          // Messages will be loaded via 'room_messages' event
        }
      }
    },
    [socket, isConnected, messages]
  );

  const leaveRoom = useCallback(
    (roomId: number) => {
      if (socket) {
        socket.emit('leave_room', { roomId });
        setCurrentRoom(null);
      }
    },
    [socket]
  );

  const markMessagesAsRead = useCallback(
    (roomId: number, messageIds: number[]) => {
      if (socket && messageIds.length > 0) {
        socket.emit('mark_messages_read', { roomId, messageIds });

        // Update local unread count
        setUnreadMessageCounts((prev) => ({
          ...prev,
          [roomId]: 0
        }));
      }
    },
    [socket]
  );

  const markNotificationAsRead = useCallback(async (notificationId: number) => {
    try {
      await chatService.markNotificationAsRead(notificationId);
      // Real-time update will be handled by socket event
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const updatePresence = useCallback(
    (status: 'online' | 'away' | 'busy') => {
      if (socket) {
        socket.emit('update_presence', { status });
      }
    },
    [socket]
  );

  const value: SocketContextType = useMemo(
    () => ({
      socket,
      isConnected,

      // Chat
      chatRooms,
      currentRoom,
      messages,
      unreadMessageCounts,

      // Notifications
      notifications,
      unreadNotificationCount,

      // Presence
      userPresence,

      // Actions
      sendMessage,
      joinRoom,
      leaveRoom,
      markMessagesAsRead,
      markNotificationAsRead,
      updatePresence,
      refreshChatRooms,

      // Loading states
      isLoadingRooms,
      isLoadingMessages,
      isLoadingNotifications
    }),
    [
      socket,
      isConnected,
      chatRooms,
      currentRoom,
      messages,
      unreadMessageCounts,
      notifications,
      unreadNotificationCount,
      userPresence,
      sendMessage,
      joinRoom,
      leaveRoom,
      markMessagesAsRead,
      markNotificationAsRead,
      updatePresence,
      refreshChatRooms,
      isLoadingRooms,
      isLoadingMessages,
      isLoadingNotifications
    ]
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketContext;
